// src/contexts/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext, useRef, } from "react";
import api, { setAccessToken, getAccessToken, refreshAccessToken } from "../api/axios";
import { ensureMySecretKeyAvailable } from "../../shared/crypto/simpleE2EE";

interface User {
  _id: string;
  profilename: string;
  email: string;
  picture?: string;
  name?: string;
  advertiserProfile?: AdvertiserProfile | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;              // access token (in-memory)
  isAuthenticated: boolean;
  isBootstrapping: boolean;          // ✅ added
  e2eeReady: boolean;
  e2eeNeedsPassword: boolean;        // always false in this model
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setE2EEPassword: (password: string | null) => void; // no-op
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

export interface AdvertiserProfile {
  accountType: "business" | "individual";
  legalName: string;
  country: string;
  city: string;
  vatId?: string | null;

  // ✅ onboarding
  completed: boolean;

  // ✅ platform control
  approved: boolean;
  suspended: boolean;

  // ✅ legal
  acceptedTerms?: {
    version: string;
    acceptedAt: string;
    country: string;
  };
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;

if (!IMAGE_CDN) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}


function formatUser(data: any): User {
  let picture = data?.picture || "";

  if (picture && picture.startsWith("/")) {
    picture = `${IMAGE_CDN}${picture}`;
  }

  return {
    ...data,
    picture,
    advertiserProfile: data?.advertiserProfile ?? null,
  };
}


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(getAccessToken());
  const [user, setUser] = useState<User | null>(null);

  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [e2eeReady, setE2eeReady] = useState(false);
  const [e2eeNeedsPassword] = useState(false);
  const bootstrappedRef = React.useRef(false);
  const didBootstrap = useRef(false); // ✅ add

  const setToken = (t: string | null) => {
    setAccessToken(t);
    setTokenState(t);
  };

  // ✅ Silent refresh on app start (cookie -> new access token)
  const refreshSession = async (): Promise<boolean> => {
    try {
      const newToken = await refreshAccessToken(); // ✅ handles csrf + refresh safely
      setToken(newToken);
      return true;
    } catch {
      setToken(null);
      setUser(null);
      return false;
    }
  };
  

  useEffect(() => {
    if (didBootstrap.current) return;
    didBootstrap.current = true;   
  
    (async () => {
      try {
        await refreshSession();
      } finally {
        setIsBootstrapping(false);
      }
    })();
        // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  

  // ✅ Whenever token changes, fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) {
        setUser(null);
        return;
      }
      try {
        const { data } = await api.get("/users/me"); // interceptor adds Authorization
        setUser(formatUser(data));
      } catch (err: any) {
        // if refresh also fails, interceptor will throw and we end here
        setUser(null);
        setToken(null);
      }
    };

    fetchProfile();
  }, [token]);

  // ✅ E2EE bootstrap (same concept)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setE2eeReady(false);
      if (!token || !user?._id) return;

      try {
        await ensureMySecretKeyAvailable();
        if (!cancelled) setE2eeReady(true);
      } catch (e) {
        if (!cancelled) setE2eeReady(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, user?._id]);

  const setE2EEPassword = (_password: string | null) => {
    // no-op
  };

  // ✅ Logout: revoke refresh cookie server-side + clear memory
  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch (err: any) {
      console.error("Logout error:", err?.message || err);
    } finally {
      localStorage.removeItem("my:secretKey");
      sessionStorage.removeItem("selectedLocation");

      setUser(null);
      setToken(null);
      setE2eeReady(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isBootstrapping,
        e2eeReady,
        e2eeNeedsPassword,
        setUser,
        setToken,
        setE2EEPassword,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export default AuthContext;
